import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, CornerDownRight, GitFork, Globe, Lock, MessageSquare, MessagesSquare, Pencil, Plus, RefreshCw, Square, Sparkles, Swords, Wrench, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  forkDeck,
  getCardMetaBatch,
  getCollection,
  getDeckBattles,
  getDeckOrPublic,
  getDeckBuildSettings,
  getDeckTrace,
  getPublicDeckTrace,
  getPublicDeckUnauth,
  listDeckAsks,
  listPublicDeckAsks,
  listDeckVersions,
  listDeckThreads,
  listPublicDeckThreads,
  listGauntletsForDeck,
  regenerateDeckStrategy,
  scoreDeckById,
  setDeckVisibility,
  listGauntletArchetypes,
  startGauntlet,
  streamAskDeck,
  type DeckAgentSummary,
  type DeckAsk,
  type DeckThreadSummary,
  type DeckBattle,
  type GauntletArchetype,
  type GauntletListItem,
  type NearestMatch,
  type ProximityScore,
} from "../lib/api";
import GatedAction from "../components/GatedAction";
import DeckCardPoolControl from "../components/DeckCardPoolControl";
import {
  readDeckCardPoolPreference,
  writeDeckCardPoolPreference,
  type CardPool,
} from "../lib/deck-card-pool";
import ActionReplayer from "../components/ActionReplayer";
import type { AgentEvent } from "../lib/types";
import DeckView from "../components/DeckView";
import SourceBadge from "../components/SourceBadge";
import AnswerSections from "../components/AnswerSections";
import ManaCurveChart from "../components/ManaCurveChart";
import ProximityBadge from "../components/ProximityBadge";
import InfoTooltip from "../components/InfoTooltip";
import ProvenanceLine from "../components/ProvenanceLine";
import ExploreDeck from "../components/ExploreDeck";
import LiquidButton from "../components/ui/LiquidButton";
import TracePanel from "../components/TracePanel";
import VersionTimeline from "../components/VersionTimeline";
import Markdown from "../components/Markdown";
import { useRefreshBalance } from "../lib/useBalance";
import { ACTION_COSTS } from "../lib/credits";
import { hasAnyNamedSection, parseAnswerSections } from "../lib/answer-sections";
import { formatCost, totalDeckCost } from "../lib/deck-price";
import { serializeDeckForArena } from "../lib/arena-format";
import { cn } from "../lib/cn";
import { winrateTone } from "../lib/winrate";

export default function DeckDetail() {
  // Route is `/decks/:owner/:slug`. We keep the local variable name `id`
  // (sourced from `slug`) to minimize diff churn — every helper /
  // sub-component below already references `id`. `params.owner` is
  // the URL-supplied owner; once the deck loads we prefer
  // `data.owner_username` (the authoritative DB value) for display.
  const { owner: ownerParam = "", slug = "" } = useParams();
  const id = slug;
  const navigate = useNavigate();
  const qc = useQueryClient();

  // PR2: this page renders for both signed-in viewers (owner OR
  // someone looking at someone else's public deck) and anonymous
  // viewers. The fetch path branches on auth state:
  //  - signed-in → owner-first then public-fallback (existing
  //    `getDeckOrPublic` — calls authed /api/decks/:id first)
  //  - signed-out → unauthenticated /api/public/decks/:owner/:slug
  // We can't call the authed endpoint anonymously (it 401s), and we
  // can't ALWAYS use the public route either (the owner viewing
  // their own private deck needs to land here too).
  const { isSignedIn, isLoaded: clerkLoaded, user } = useUser();
  const myUsername = user?.username ?? null;

  const [exploreOpen, setExploreOpen] = useState(false);
  const [exploreWelcome, setExploreWelcome] = useState(false);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const askSectionRef = useRef<HTMLDivElement>(null);

  // Distinct cache key from DeckEdit's ["deck", id]. DeckDetail wraps
  // the response in { detail, isOwner, otherMatches } via getDeckOrPublic;
  // DeckEdit calls getDeck which returns the flat SavedDeckDetail.
  // Sharing the key meant whichever page rendered first poisoned the
  // other with the wrong shape, blanking the page after a save round
  // trip.
  //
  // For anonymous viewers we hit /api/public/decks/:owner/:slug
  // directly. The shape is the same SavedDeckDetail; we wrap it into
  // the same { detail, isOwner, otherMatches } envelope for parity
  // with the signed-in path. `isOwner` is derived later from
  // `auth.username === detail.owner_username`, not the response.
  const deckQuery = useQuery({
    queryKey: ["deck-detail", ownerParam, id, isSignedIn ? "auth" : "anon"],
    queryFn: async () => {
      if (isSignedIn) {
        return await getDeckOrPublic(id);
      }
      const detail = await getPublicDeckUnauth(ownerParam, id);
      return { detail, isOwner: false, otherMatches: [] };
    },
    // Only enable once Clerk has settled. Firing while isLoaded is
    // false would race the auth state and we'd potentially call the
    // authed path with no token (or vice versa).
    enabled: !!id && !!ownerParam && clerkLoaded,
  });
  const data = deckQuery.data?.detail;
  const otherMatches = deckQuery.data?.otherMatches ?? [];
  const isLoading = deckQuery.isLoading || !clerkLoaded;
  const error = deckQuery.error;
  // Keep server-rendered public content visible until the interactive page
  // has data. Slow/blocked auth scripts or failed fetches must not erase it.
  useEffect(() => {
    if (!data) return;
    document.getElementById("public-deck-fallback")?.remove();
    document.getElementById("public-fallback-style")?.remove();
  }, [data]);
  const ownerUsername = data?.owner_username;
  // Owner detection: derive from auth context vs the deck's
  // owner_username (the server's factual, unforgeable owner string).
  // Never trust the server-supplied isOwner flag from the wrapper —
  // the public path always returns false even when the URL's :owner
  // matches the signed-in user, and we want the owner experience to
  // light up immediately on those URLs.
  const isOwner = !!myUsername && !!ownerUsername && myUsername === ownerUsername;

  const handleCloseExplore = () => {
    setExploreOpen(false);
    setExploreWelcome(false);
  };

  const handleOpenExplore = () => {
    askSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scoreMutation = useMutation({
    mutationFn: () => scoreDeckById(id),
  });

  // Auto-fire scoring once on mount (per deck id) so nearest-matches
  // populate without a manual click. The persisted score + breakdown
  // render instantly from data.proximity_score; this fills in the
  // candidates/weights line and the nearest-matches list. Cheap (~1-2s
  // local DB work, no LLM cost) so doing it on every visit is fine.
  // Skipped on non-owner views — the score panel only shows for owners.
  const autoScoredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isOwner) return;
    if (!data) return;
    if (autoScoredFor.current === id) return;
    autoScoredFor.current = id;
    scoreMutation.mutate();
    // scoreMutation reference is stable enough to omit; we key on id
    // changes via the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isOwner, data]);

  // Fork: clones the current deck (rows + summary + asks) into the
  // caller's account and navigates to the freshly-minted copy. Source
  // owner is forwarded so the server can resolve another user's public
  // deck. Errors surface inline; success leaves the user on /decks/me/<new>.
  const forkMutation = useMutation({
    mutationFn: () => forkDeck(id, ownerParam),
    onSuccess: ({ id: newSlug, owner_username }) => {
      // Invalidate the saved-decks list so /decks shows the fork on
      // arrival instead of forcing a hard refresh. Same pattern as the
      // visibility-toggle mutation below.
      void qc.invalidateQueries({ queryKey: ["decks"] });
      navigate(`/decks/${owner_username}/${newSlug}`);
    },
  });
  const handleFork = (): void => {
    if (forkMutation.isPending) return;
    forkMutation.mutate();
  };

  const names = useMemo(() => {
    const set = new Set<string>();
    if (data?.deck) {
      for (const e of data.deck.mainboard) set.add(e.name);
      for (const e of data.deck.sideboard) set.add(e.name);
    }
    return [...set];
  }, [data]);

  const metaQuery = useQuery({
    queryKey: ["card-meta-batch", names],
    queryFn: () => getCardMetaBatch(names),
    enabled: names.length > 0,
  });

  // Pick the trace endpoint by viewer:
  //  - owner (signed in, looking at their own deck) → authed
  //    /api/decks/:id/trace, full payload including image-context OCR
  //  - non-owner OR anonymous → public /api/public/decks/:owner/:slug/trace,
  //    which strips image_context.extracted_text server-side
  // Both endpoints return the same { events, count } shape so the
  // downstream TracePanel doesn't care which side it came from.
  const traceQuery = useQuery({
    queryKey: ["deck-trace", ownerParam, id, isOwner ? "auth" : "public"],
    queryFn: () =>
      isOwner ? getDeckTrace(id) : getPublicDeckTrace(ownerParam, id),
    enabled: !!id && !!ownerParam && clerkLoaded,
    // Trace is immutable once a deck is saved — no need to refetch.
    staleTime: Infinity,
  });
  // Owner-only build settings (exemplar mode + progressive discovery
  // toggles persisted on deck_builds). The classifier and image-context
  // signals already come through traceEvents, so this query is the
  // missing piece TracePanel needs to render the full settings header.
  const buildSettingsQuery = useQuery({
    queryKey: ["deck-build-settings", id],
    queryFn: () => getDeckBuildSettings(id),
    enabled: !!id && isOwner,
    staleTime: Infinity,
  });

  const parsedSections = useMemo(
    () => (data?.answer ? parseAnswerSections(data.answer) : null),
    [data?.answer],
  );

  // Prefer the server-computed `card_cost_usd` (ships in the /api/decks/:id
  // response), falling back to the client batch calc if the server field is
  // unavailable. Either way the visual is the same.
  const totalCost = useMemo(() => {
    if (data && typeof data.card_cost_usd === "number") {
      return { usd: data.card_cost_usd, missing: data.card_cost_missing ?? 0 };
    }
    if (!data?.deck || !metaQuery.data) return null;
    return totalDeckCost(data.deck.mainboard, data.deck.sideboard, metaQuery.data);
  }, [data, metaQuery.data]);

  // PR2: mode flips to "public" for any viewer who isn't the owner.
  // That covers anonymous viewers AND signed-in viewers looking at
  // someone else's public deck. mode="owner" is reserved for the
  // single case where the signed-in user matches the URL's :owner
  // segment (and the deck loaded successfully — server still does
  // its own auth check on the authed path, so we won't accidentally
  // render owner mode for a deck the server refused to return).
  const mode: "owner" | "public" = isOwner ? "owner" : "public";

  // Pending ask queued by an anonymous click that signed up. The
  // ActionReplayer hands it off through state so AskSection can
  // pre-fill the textarea + auto-fire `start()` on its next render.
  // Cleared back to null after AskSection consumes it.
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  const consumePendingAsk = (): string | null => {
    if (pendingAsk == null) return null;
    const q = pendingAsk;
    setPendingAsk(null);
    return q;
  };

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-8">
      {/* Replays the localStorage-queued GatedAction (if any) once
          Clerk reports signed-in. The "ask" handler stashes the
          question in state so AskSection's next render pre-fills its
          textarea and auto-submits. "playtest" and "build-similar"
          are stubbed for now — the slot still gets cleared so it
          doesn't dangle until TTL. */}
      {ownerParam && id && (
        <ActionReplayer
          owner={ownerParam}
          deckId={id}
          handlers={{
            ask: (q) => {
              const question = String((q.payload as { question?: unknown }).question ?? "");
              if (question.length > 0) setPendingAsk(question);
            },
            // Anonymous user clicked Playtest → signed up → we're
            // back on the deck page. Open the ExploreDeck modal which
            // hosts the starting-hand sim. Welcome flag is on so the
            // first visit reads as a guided experience instead of
            // dumping them into the bare modal.
            playtest: () => {
              setExploreOpen(true);
              setExploreWelcome(true);
            },
            // Anonymous "build similar" click → signed up → land on
            // the BuildDeck page with the source deck's brief
            // pre-filled. BuildDeck already consumes ?brief= via
            // useSearchParams (web/src/pages/BuildDeck.tsx:57). We
            // pass the source deck's brief verbatim so the user lands
            // on a populated form they can edit before submitting.
            "build-similar": (q) => {
              const brief = String(
                (q.payload as { brief?: unknown }).brief ?? "",
              );
              const params = new URLSearchParams();
              if (brief) params.set("brief", brief);
              navigate(`/?${params.toString()}`);
            },
            // Anonymous user clicked "Save to my decks" → signed up →
            // auto-fork. We re-issue the mutation here so the new
            // signed-in session does the actual copy. The mutation's
            // onSuccess navigates to the fresh /decks/<me>/<slug>.
            fork: () => {
              if (!forkMutation.isPending) forkMutation.mutate();
            },
          }}
        />
      )}
      <div className="flex items-center justify-between gap-4">
        {/* Hide the back-to-list link for anonymous viewers — /decks
            is the signed-in saved-decks page, not a public index, so
            clicking it just bounces to Landing. Signed-in viewers
            (owner or otherwise) get the link as before. */}
        {isSignedIn ? (
          <Link
            to="/decks"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            All saved decks
          </Link>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          {data?.deck && <CopyDeckButton deck={data.deck} name={data.theme} />}
          {/* Save to my decks — quiet text-link variant for skimmers
              who don't scroll to the bottom CTA. Hidden for the deck's
              owner (no value in cloning your own deck inline; bottom
              CTA still offers it). For anonymous viewers we wrap in
              GatedAction so the click signs them up and the post-
              signup ActionReplayer fires the fork. */}
          {data && !isOwner && (
            isSignedIn ? (
              <button
                type="button"
                onClick={handleFork}
                disabled={forkMutation.isPending}
                className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors disabled:opacity-50"
                title="Clone this deck into your account — keeps the AI summary and questions."
              >
                <GitFork size={13} strokeWidth={1.75} />
                {forkMutation.isPending ? "Saving…" : "Save to my decks"}
              </button>
            ) : (
              <GatedAction
                action="fork"
                payload={{}}
                deckId={id}
                owner={ownerUsername ?? ownerParam}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
                  title="Clone this deck into your account — keeps the AI summary and questions."
                >
                  <GitFork size={13} strokeWidth={1.75} />
                  Save to my decks
                </button>
              </GatedAction>
            )
          )}
          {mode === "owner" && data && isOwner && data.role !== "meta_reference" && (
            <VisibilityToggle slug={id} isPublic={!!data.is_public} />
          )}
          {mode === "owner" && data?.deck && isOwner && ownerUsername && (
            <Link
              to={`/decks/${ownerUsername}/${id}/edit`}
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Pencil size={13} strokeWidth={1.75} />
              Edit deck
            </Link>
          )}
        </div>
      </div>

      {isLoading && <div className="skeleton h-16 w-full" />}
      {error && (
        <div className="surface p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {data && (
        <DeckBody
          mode={mode}
          data={data}
          id={id}
          isOwner={isOwner}
          otherMatches={otherMatches}
          ownerUsername={ownerUsername ?? ownerParam}
          meta={metaQuery.data}
          traceEvents={traceQuery.data?.events ?? []}
          buildSettings={buildSettingsQuery.data?.settings}
          parsedSections={parsedSections}
          totalCost={totalCost}
          sectionsRef={sectionsRef}
          askSectionRef={askSectionRef}
          onOpenExplore={handleOpenExplore}
          scoreLoading={scoreMutation.isPending}
          scoreError={scoreMutation.error as Error | null}
          score={scoreMutation.data?.score ?? null}
          onPlaytest={() => {
            setExploreOpen(true);
            setExploreWelcome(false);
          }}
          onFork={handleFork}
          forkPending={forkMutation.isPending}
          forkError={(forkMutation.error as Error | null) ?? null}
          consumePendingAsk={consumePendingAsk}
        />
      )}

      {data?.deck && (
        <ExploreDeck
          open={exploreOpen}
          deck={data.deck}
          title={data.theme}
          onClose={handleCloseExplore}
          welcome={exploreWelcome}
        />
      )}
    </div>
  );
}

// Pure render of the deck-detail page body. PR1 extracts this from
// DeckDetail without behavior change so PR2 can mount a public route
// (`mode="public"`) without churning the owner path. mode="public" is
// dead code in PR1 — no live route reaches it yet.
//
// All file-local sub-components (ScoreSection, AskSection, VersionTimeline,
// VisibilityToggle, RecentBattles, PublicMatchesSwitcher, etc) stay in
// DeckDetail.tsx; we just thread the data they need through this wrapper.
function DeckBody({
  mode,
  data,
  id,
  isOwner,
  otherMatches,
  ownerUsername,
  meta,
  traceEvents,
  buildSettings,
  parsedSections,
  totalCost,
  sectionsRef,
  askSectionRef,
  onOpenExplore,
  scoreLoading,
  scoreError,
  score,
  onPlaytest,
  onFork,
  forkPending,
  forkError,
  consumePendingAsk,
}: {
  mode: "owner" | "public";
  data: import("../lib/api").SavedDeckDetail;
  id: string;
  isOwner: boolean;
  otherMatches: Array<{ owner_username: string; slug: string; theme: string }>;
  ownerUsername: string | undefined;
  meta: Record<string, import("../lib/api").CardMeta> | undefined;
  traceEvents: AgentEvent[];
  buildSettings: import("../lib/api").DeckBuildSettings | undefined;
  parsedSections: ReturnType<typeof parseAnswerSections> | null;
  totalCost: { usd: number; missing: number } | null;
  sectionsRef: React.RefObject<HTMLDivElement>;
  askSectionRef: React.RefObject<HTMLDivElement>;
  onOpenExplore: () => void;
  scoreLoading: boolean;
  scoreError: Error | null;
  score: ProximityScore | null;
  onPlaytest: () => void;
  onFork: () => void;
  forkPending: boolean;
  forkError: Error | null;
  /** Read-and-clear of the queued ask question after sign-up. The
   *  AskSection consumes it once on mount and pre-fills + auto-fires
   *  start(). Returning null means no pending action. */
  consumePendingAsk: () => string | null;
}) {
  // mode="public" hides every owner-only affordance. The remaining
  // gates (`isOwner` for ask history, score, battles, debug panels) are
  // a defense-in-depth against a future bug where mode="public" is
  // mounted with a request that somehow resolves the viewer as owner.
  const isPublicView = mode === "public";
  const sourceCollectionId = data.collection_id;
  const { data: sourceCollection } = useQuery({
    queryKey: ["collection", sourceCollectionId],
    queryFn: () => getCollection(sourceCollectionId!),
    enabled: isOwner && !!sourceCollectionId,
    retry: false,
  });

  return (
    <>
      <header className="space-y-4">
        <div className="eyebrow flex items-center gap-2">
          <span>Deck</span>
          {data.role === "meta_reference" && (
            <span
              className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400"
              title="Curated meta archetype used by the gauntlet"
            >
              meta archetype
            </span>
          )}
          {!isOwner && ownerUsername && (
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
              · public · by{" "}
              <span className="font-mono normal-case text-fg-muted">
                {ownerUsername}
              </span>
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
            {data.theme}
          </h1>
          <ProximityBadge score={data.proximity_score} size="md" />
          {totalCost && totalCost.usd > 0 && (
            <span
              className="ml-auto font-mono text-xl tabular-nums text-fg-muted"
              title={
                totalCost.missing > 0
                  ? `Card total at latest Scryfall price snapshot · ${totalCost.missing} copies missing price data`
                  : "Card total at latest Scryfall price snapshot"
              }
            >
              {formatCost(totalCost)}
            </span>
          )}
        </div>
        <p className="text-sm text-fg-muted leading-relaxed max-w-2xl">
          {data.brief}
        </p>
        {sourceCollectionId && (
          <p className="text-xs text-fg-subtle">
            Built from: {isOwner ? (
              <Link to="/collections" className="text-accent hover:underline">
                {sourceCollection?.name ?? "Original collection"}
              </Link>
            ) : "a collection"}
          </p>
        )}
        <ProvenanceLine
          fields={[
            { value: new Date(data.created_at).toLocaleString() },
            { label: "built", value: `${(data.elapsed_ms / 1000).toFixed(1)}s` },
            { label: "model", value: data.model },
            ...(data.prompt_version
              ? [{
                  label: "prompt",
                  value: typeof data.cost_estimate_usd === "number"
                    ? `${data.prompt_version} ($${data.cost_estimate_usd.toFixed(2)})`
                    : data.prompt_version,
                }]
              : []),
          ]}
        />
      </header>

      {data.agent_summary && <AgentSummarySection summary={data.agent_summary} />}

      {data.deck ? (
        <DeckView
          deck={data.deck}
          meta={meta}
          sideboardFooter={
            <div className="flex justify-center">
              <LiquidButton
                className="h-10 text-sm"
                onClick={onOpenExplore}
                trailing={<Sparkles size={14} strokeWidth={2} />}
              >
                Explore deck
              </LiquidButton>
            </div>
          }
        />
      ) : (
        <div className="surface p-4 text-sm text-warn">
          Couldn't parse a decklist out of this saved answer.
        </div>
      )}

      {/* Version history. Owners see it expanded inline (their own
          iteration story). Public viewers see it collapsed by default
          (supporting context, not the conversion path) — the timeline
          itself handles the <details> wrapper based on publicOwner. */}
      <VersionTimeline
        deckId={id}
        isOwner={!isPublicView && isOwner}
        publicOwner={isPublicView ? ownerUsername : undefined}
      />

      {/* Ask sits between version history and the deeper analysis
          sections. The wrapping ref is the scroll target for the
          "Explore deck" button — clicking it now slides the user down
          to the Ask textarea instead of the AnswerSections block. */}
      <div ref={askSectionRef} className="space-y-3">
        <AskSection
          key={`${ownerUsername ?? ""}:${id}:${isOwner ? "owner" : "public"}`}
          deckId={id}
          sourceCollectionId={sourceCollectionId}
          sourceCollectionName={sourceCollection?.name}
          isOwner={isOwner}
          ownerUsername={ownerUsername}
          publicAsks={isPublicView}
          consumePendingAsk={consumePendingAsk}
        />
        {isPublicView && ownerUsername && (
          <PublicAskHistory ownerUsername={ownerUsername} slug={id} />
        )}
      </div>

      {!isPublicView && isOwner && (
        <RegenStrategySection deckId={id} />
      )}

      {parsedSections && hasAnyNamedSection(parsedSections) && (
        <div ref={sectionsRef}>
          <AnswerSections
            parsed={parsedSections}
            manaBaseExtras={
              data.deck && parsedSections.manaBase ? (
                <ManaCurveChart
                  mainboard={data.deck.mainboard}
                  meta={meta}
                />
              ) : undefined
            }
          />
        </div>
      )}

      {!isPublicView && isOwner && (
        <ScoreSection
          loading={scoreLoading}
          error={scoreError}
          score={score}
          persistedScore={data.proximity_score ?? null}
          persistedBreakdown={data.proximity_breakdown}
        />
      )}

      {!isPublicView && isOwner && data.role === "user" && (
        <PastGauntlets deckId={id} />
      )}

      {!isPublicView && isOwner && <RecentBattles deckId={id} />}

      {/* Multiple public decks share this slug — rare, but surface
          alternatives as a chooser so the user can switch. */}
      {!isOwner && otherMatches.length > 0 && (
        <PublicMatchesSwitcher matches={otherMatches} currentSlug={id} />
      )}

      {/* Agent trace surfaces on every viewer (owner + public) — it's
          the strongest provenance signal for "where did this build come
          from". The public endpoint redacts image_context.extracted_text
          server-side; the owner endpoint returns it. buildSettings is
          owner-only (the /build-settings endpoint is authed), so on
          public views the TracePanel renders without the settings
          header — the classifier and image-count chips it already
          derives from the events themselves still appear. */}
      {traceEvents.length > 0 && (
        <details className="surface p-4">
          <summary className="cursor-pointer text-sm text-fg-muted">
            Agent trace ({traceEvents.length} events)
          </summary>
          <div className="mt-4">
            <TracePanel
              events={traceEvents}
              settings={isOwner ? buildSettings : undefined}
            />
          </div>
        </details>
      )}

      {!isPublicView && (
        <details className="surface p-4">
          <summary className="cursor-pointer text-sm text-fg-muted">
            Raw agent answer (copy-friendly)
          </summary>
          <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed mt-3 text-fg-muted font-mono">
            {data.answer}
          </pre>
        </details>
      )}

      {data.deck && (
        <div className="flex flex-wrap justify-center gap-3 pt-4">
          {isPublicView ? (
            <GatedAction
              action="playtest"
              payload={{ deckId: id }}
              deckId={id}
              owner={ownerUsername ?? ""}
            >
              <LiquidButton
                className="h-10 text-sm"
                onClick={onPlaytest}
                trailing={<Sparkles size={14} strokeWidth={2} />}
              >
                Playtest starting hand
              </LiquidButton>
            </GatedAction>
          ) : (
            <LiquidButton
              className="h-10 text-sm"
              onClick={onPlaytest}
              trailing={<Sparkles size={14} strokeWidth={2} />}
            >
              Playtest starting hand
            </LiquidButton>
          )}
          {/* Gauntlet runs the deck against the 3 curated meta_reference
              archetypes. Owner-only on user-role decks: meta archetypes
              can't gauntlet themselves, and non-owners can't charge the
              owner's account. */}
          {!isPublicView && isOwner && data.role === "user" && (
            <RunGauntletButton slug={id} />
          )}
        </div>
      )}

      {/* Bottom-of-page conversion CTA. Non-owners only — owners get
          no value from cloning their own deck inline. The tagline sells
          the value (free, summary preserved, no build cost) so readers
          who actually scrolled to the bottom convert here. The header
          link earlier covers skimmers. GatedAction wraps it so anonymous
          viewers click → sign up → ActionReplayer auto-forks. */}
      {data.deck && !isOwner && (
        <div className="mt-2 flex flex-col items-center gap-2 pt-2">
          <GatedAction
            action="fork"
            payload={{}}
            deckId={id}
            owner={ownerUsername ?? ""}
          >
            <LiquidButton
              className="h-10 text-sm"
              onClick={onFork}
              disabled={forkPending}
              trailing={<GitFork size={14} strokeWidth={2} />}
            >
              {forkPending ? "Saving deck…" : "Save this deck — free"}
            </LiquidButton>
          </GatedAction>
          <p className="max-w-md text-center text-xs text-fg-subtle leading-relaxed">
            Save your own copy. Keep the AI summary and Q&amp;A; ask
            your own questions; edit it freely. No build cost.
          </p>
          {forkError && (
            <span className="text-xs text-danger" role="alert">
              {forkError.message}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function AgentSummarySection({ summary }: { summary: DeckAgentSummary }) {
  // Defense-in-depth: server already returns undefined for all-empty
  // summaries, but a degenerate writer could send empty strings.
  const hasAny =
    summary.archetype_guess ||
    summary.curve_summary ||
    summary.win_condition ||
    summary.key_creatures.length > 0 ||
    summary.key_spells.length > 0 ||
    summary.sideboard_themes.length > 0;
  if (!hasAny) return null;
  return (
    <section className="surface p-5 space-y-3">
      <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
        Agent summary
      </h2>
      {summary.archetype_guess && (
        <div className="text-base">
          <span className="text-fg-subtle text-xs uppercase tracking-wider mr-2">
            Archetype
          </span>
          <span className="font-medium">{summary.archetype_guess}</span>
        </div>
      )}
      {summary.curve_summary && (
        <p className="text-sm text-fg-muted leading-relaxed">
          {summary.curve_summary}
        </p>
      )}
      {summary.win_condition && (
        <p className="text-sm leading-relaxed">
          <span className="text-fg-subtle text-xs uppercase tracking-wider mr-2">
            Win con
          </span>
          {summary.win_condition}
        </p>
      )}
      {summary.key_creatures.length > 0 && (
        <p className="text-sm">
          <span className="text-fg-subtle text-xs uppercase tracking-wider mr-2">
            Key creatures
          </span>
          {summary.key_creatures.join(", ")}
        </p>
      )}
      {summary.key_spells.length > 0 && (
        <p className="text-sm">
          <span className="text-fg-subtle text-xs uppercase tracking-wider mr-2">
            Key spells
          </span>
          {summary.key_spells.join(", ")}
        </p>
      )}
      {summary.sideboard_themes.length > 0 && (
        <p className="text-sm">
          <span className="text-fg-subtle text-xs uppercase tracking-wider mr-2">
            Sideboard themes
          </span>
          {summary.sideboard_themes.join("; ")}
        </p>
      )}
    </section>
  );
}

function RunGauntletButton({ slug }: { slug: string }) {
  const navigate = useNavigate();
  // Optional override of the meta_reference roster. null = use server
  // default (the 3 currently-active archetypes); a non-empty array =
  // play against this exact roster (mix of active and historical).
  const [customRoster, setCustomRoster] = useState<string[] | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      startGauntlet(slug, 10, customRoster ?? undefined),
    onSuccess: ({ gauntlet_id }) => {
      navigate(`/mega-build/gauntlet/${gauntlet_id}`);
    },
  });
  const errMsg = mutation.error ? (mutation.error as Error).message : null;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <LiquidButton
        className="h-10 text-sm"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        trailing={<Swords size={14} strokeWidth={2} />}
      >
        {mutation.isPending ? "Starting gauntlet…" : "Run Gauntlet"}
      </LiquidButton>
      <GauntletOpponentsSelector
        value={customRoster}
        onChange={setCustomRoster}
        disabled={mutation.isPending}
      />
      {errMsg && (
        <span className="text-xs text-danger" role="alert">
          {errMsg}
        </span>
      )}
    </div>
  );
}

// Disclosure under the "Run Gauntlet" button. Default state (closed
// summary line: "vs current meta") sends no roster override, so the
// server picks the 3 currently-active archetypes — same as before.
// Open state lets the user check/uncheck archetypes and mix in
// historical metas. Selecting any subset that differs from the active
// 3 enables the override; clicking "Reset to current meta" clears it.
function GauntletOpponentsSelector({
  value,
  onChange,
  disabled,
}: {
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  disabled?: boolean;
}) {
  const query = useQuery({
    queryKey: ["gauntlet-archetypes"],
    queryFn: listGauntletArchetypes,
    staleTime: 60_000,
  });
  const archetypes = query.data?.archetypes ?? [];
  const active = archetypes.filter((a) => a.is_active);
  const historical = archetypes.filter((a) => !a.is_active);
  const selectedIds = new Set(value ?? active.map((a) => a.id));
  const toggle = (id: string): void => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const arr = Array.from(next);
    // If the resulting set is exactly the active 3, treat that as
    // "default" and clear the override so the server picks fresh on
    // next seed.
    const isDefault =
      arr.length === active.length &&
      active.every((a) => next.has(a.id));
    onChange(isDefault ? null : arr);
  };
  if (query.isLoading) return null;
  if (archetypes.length === 0) return null;
  const overrideOn = value !== null;
  const summary = overrideOn
    ? `${selectedIds.size} custom opponent${selectedIds.size === 1 ? "" : "s"}`
    : "vs current meta";
  return (
    <details className="group text-xs">
      <summary className="list-none cursor-pointer inline-flex items-center gap-1 text-fg-subtle hover:text-fg-muted transition-colors select-none">
        <span className="font-mono group-open:rotate-90 transition-transform inline-block w-2.5">
          ›
        </span>
        Opponents:{" "}
        <span className={cn(overrideOn ? "text-accent" : "text-fg-muted")}>
          {summary}
        </span>
      </summary>
      <div className="mt-2 max-w-sm rounded-md border border-border bg-bg-raised/50 p-3 space-y-2.5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-fg-subtle mb-1.5">
            Current meta
          </div>
          <ul className="space-y-1">
            {active.map((a) => (
              <ArchetypeRow
                key={a.id}
                archetype={a}
                checked={selectedIds.has(a.id)}
                disabled={disabled}
                onToggle={() => toggle(a.id)}
              />
            ))}
          </ul>
        </div>
        {historical.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-fg-subtle mb-1.5">
              Historical
            </div>
            <ul className="space-y-1">
              {historical.map((a) => (
                <ArchetypeRow
                  key={a.id}
                  archetype={a}
                  checked={selectedIds.has(a.id)}
                  disabled={disabled}
                  onToggle={() => toggle(a.id)}
                />
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/60 text-[11px]">
          <span className="text-fg-subtle">
            Pick 1–5 opponents.
          </span>
          {overrideOn && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-accent hover:underline"
              disabled={disabled}
            >
              Reset to current meta
            </button>
          )}
        </div>
      </div>
    </details>
  );
}

function ArchetypeRow({
  archetype,
  checked,
  disabled,
  onToggle,
}: {
  archetype: GauntletArchetype;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const date = new Date(archetype.created_at).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return (
    <li>
      <label
        className={cn(
          "flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer",
          "hover:bg-bg-base/60 transition-colors",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <input
          type="checkbox"
          className="accent-accent"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
        />
        <span className="flex-1 truncate text-fg">
          {archetype.archetype_guess ?? archetype.theme}
        </span>
        <span className="font-mono text-[10px] text-fg-subtle">{date}</span>
      </label>
    </li>
  );
}

// Read-only ask thread for the public deck page. Hits the new
// `/api/public/decks/:owner/:slug/asks` endpoint (no auth required).
// We don't render this in mode="owner" — owner mode already shows the
// thread via the authed AskSection's history list.
function PublicAskHistory({
  ownerUsername,
  slug,
}: {
  ownerUsername: string;
  slug: string;
}) {
  // Anonymous viewers fetch threads + asks via the public route. We
  // need both because ThreadList renders nested messages by filtering
  // the asks list by thread_id. Two parallel queries keep the cache
  // shape symmetrical with the owner path.
  const asksQuery = useQuery({
    queryKey: ["public-deck-asks", ownerUsername, slug],
    queryFn: () => listPublicDeckAsks(ownerUsername, slug),
    staleTime: 60_000,
  });
  const threadsQuery = useQuery({
    queryKey: ["public-deck-threads", ownerUsername, slug],
    queryFn: () => listPublicDeckThreads(ownerUsername, slug),
    staleTime: 60_000,
  });
  if (!asksQuery.data || !threadsQuery.data) return null;
  if (threadsQuery.data.count === 0) return null;
  // Public viewers can't reply or apply suggestions to someone else's
  // deck — onReply is a no-op (the Reply button is gated on isOwner
  // anyway, but the prop is required by ThreadList).
  return (
    <ThreadList
      threads={threadsQuery.data.threads}
      asks={asksQuery.data.asks}
      deckId={slug}
      ownerUsername={ownerUsername}
      isOwner={false}
      activeParentAskId={null}
      onReply={() => {}}
    />
  );
}

function ScoreSection({
  loading,
  error,
  score,
  persistedScore,
  persistedBreakdown,
}: {
  loading: boolean;
  error: Error | null;
  score: ProximityScore | null;
  persistedScore: number | null;
  persistedBreakdown:
    | { jaccard: number; core_cards: number; mana_base: number; scored_at: string }
    | undefined;
}) {
  // Live rescore wins over persisted on render; persisted values come
  // from the deck row written at save/upload/backfill time so the panel
  // shows on first paint without a click. Edits null both columns in
  // lockstep so we can trust the pair to be either both-set or both-null.
  const headlineScore = score?.score ?? persistedScore;
  const headlineDimensions = score?.dimensions ?? persistedBreakdown ?? null;
  if (headlineScore == null && !loading && !error) return null;

  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Tournament proximity
        </h2>
      </header>
      {loading && <div className="skeleton h-32 w-full" />}
      {error && (
        <div className="surface p-4 text-sm text-danger">{error.message}</div>
      )}
      {headlineScore != null && headlineDimensions && (
        <div className="surface p-5 space-y-5">
          <div className="flex items-baseline gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-fg-subtle">
                Score
                <InfoTooltip
                  label="What is the meta score?"
                  content={
                    "How close this deck is to the nearest competitive lists in our tournament corpus. " +
                    "Each candidate exemplar is scored on three dimensions — " +
                    "J = Card overlap (50% of the blend, copy-aware Jaccard with basics excluded), " +
                    "C = Core cards (30%, % of the exemplar's top-10 most-played cards present), " +
                    "M = Mana base (20%, blend of non-basic land overlap, total land count, and basic-vs-nonbasic ratio) " +
                    "— then weighted by source tier (MTGO and MTGTop8 outrank community brews). " +
                    "60+ is tier-shaped; <40 is brewing space."
                  }
                  iconSize={11}
                />
              </div>
              <div className="font-mono text-3xl tabular-nums tracking-tight mt-0.5">
                {headlineScore.toFixed(1)}
                <span className="text-fg-subtle text-base ml-1">/100</span>
              </div>
            </div>
            {score && (
              <div className="text-xs text-fg-subtle pl-4 border-l border-border">
                <div>{score.n_candidates} candidate exemplars</div>
                <div className="font-mono tabular-nums">
                  weights: J={score.weights.jaccard} C={score.weights.core_cards} M={score.weights.mana_base}
                </div>
              </div>
            )}
          </div>

          <DimensionBars dimensions={headlineDimensions} />

          {score && score.nearest.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-fg-subtle mb-2">
                Nearest matches
              </div>
              <ol className="space-y-2">
                {score.nearest.slice(0, 5).map((m) => (
                  <NearestRow key={m.exemplar_id} m={m} />
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function DimensionBars({
  dimensions,
}: {
  dimensions: { jaccard: number; core_cards: number; mana_base: number };
}) {
  const rows = [
    {
      key: "J",
      label: "Card overlap",
      value: dimensions.jaccard,
      hint: "J — copy-aware Jaccard against the nearest exemplar (basics excluded)",
    },
    {
      key: "C",
      label: "Core cards",
      value: dimensions.core_cards,
      hint: "C — % of the nearest exemplar's top-10 most-played cards present",
    },
    {
      key: "M",
      label: "Mana base",
      value: dimensions.mana_base,
      hint: "M — blend of non-basic land overlap, total land count, and basic-vs-nonbasic ratio",
    },
  ] as const;
  return (
    <div className="flex flex-col gap-1.5 max-w-md w-full">
      {rows.map((r) => {
        const v = Math.max(0, Math.min(100, r.value));
        const tone = v >= 60 ? "accent" : v >= 40 ? "muted" : "subtle";
        return (
          <div key={r.label} className="flex items-center gap-3 text-xs" title={r.hint}>
            <span className="w-32 shrink-0 text-fg-muted">
              <span className="font-mono text-fg-subtle mr-1.5">{r.key}</span>
              {r.label}
            </span>
            <div className="relative h-1.5 flex-1 min-w-[80px] overflow-hidden rounded-sm bg-bg-raised">
              <div
                className={cn(
                  "absolute inset-y-0 left-0 rounded-sm",
                  tone === "accent" && "bg-accent/70",
                  tone === "muted" && "bg-fg-muted/50",
                  tone === "subtle" && "bg-fg-subtle/40",
                )}
                style={{ width: `${v}%` }}
              />
            </div>
            <span
              className={cn(
                "font-mono tabular-nums w-10 text-right",
                tone === "accent" && "text-accent",
                tone === "muted" && "text-fg-muted",
                tone === "subtle" && "text-fg-subtle",
              )}
            >
              {r.value.toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function NearestRow({ m }: { m: NearestMatch }) {
  return (
    <li className="grid grid-cols-[auto_1fr_auto] items-center gap-3 p-2 rounded-md hover:bg-bg-raised/50 transition-colors">
      <div className="font-mono text-sm tabular-nums text-fg">
        {m.tier_scaled.toFixed(1)}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <SourceBadge source={m.source} />
          <a
            href={m.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm truncate hover:text-accent"
          >
            {m.deck_name || m.author || m.exemplar_id}
          </a>
        </div>
        <div className="mt-1 font-mono text-[11px] text-fg-subtle tabular-nums">
          J={m.dimensions.jaccard.toFixed(1)} · C={m.dimensions.core_cards.toFixed(1)} · M={m.dimensions.mana_base.toFixed(1)} · tier={m.tier_weight.toFixed(1)}
        </div>
      </div>
      <div className={cn(
        "text-[11px] text-fg-subtle",
        m.author ? "" : "opacity-0",
      )}>
        {m.author ?? "?"}
      </div>
    </li>
  );
}

function PastGauntlets({ deckId }: { deckId: string }) {
  const query = useQuery({
    queryKey: ["deck-gauntlets", deckId],
    queryFn: () => listGauntletsForDeck(deckId),
    enabled: !!deckId,
    // Past gauntlets only mutate when the user finishes a new run,
    // which happens minutes apart at best. 30s staleTime keeps the
    // section quiet on tab switches while still refreshing on revisit.
    staleTime: 30_000,
  });

  if (!query.data || query.data.count === 0) return null;

  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Past gauntlets
        </h2>
      </header>
      <ul className="surface divide-y divide-border">
        {query.data.gauntlets.map((g) => (
          <GauntletRow key={g.id} g={g} />
        ))}
      </ul>
    </section>
  );
}

function GauntletRow({ g }: { g: GauntletListItem }) {
  const winrate = g.winrate !== null ? parseFloat(g.winrate) : null;
  const pct = winrate !== null ? winrate * 100 : null;
  const tone = winrateTone(pct);
  const statusTone =
    g.status === "complete"
      ? "text-accent"
      : g.status === "error"
        ? "text-danger"
        : "text-warn";

  return (
    <li>
      <Link
        to={`/mega-build/gauntlet/${encodeURIComponent(g.id)}`}
        className="flex items-center gap-3 px-4 py-3 hover:bg-bg-raised/30 transition-colors"
      >
        <Swords size={14} strokeWidth={1.75} className="text-fg-subtle" />
        <span
          className={cn(
            "font-mono tabular-nums text-sm w-20 text-right",
            tone,
          )}
        >
          {pct !== null ? `${pct.toFixed(0)}%` : "—"}
        </span>
        <span className="text-[11px] text-fg-subtle uppercase tracking-wider">
          {g.total_user_wins}/{g.total_games}
        </span>
        <span className="flex-1 truncate text-sm text-fg-muted">
          vs top-meta · {g.games_per_archetype}g per archetype
        </span>
        <span className={cn("font-mono text-[11px] uppercase tracking-wider", statusTone)}>
          {g.status}
        </span>
        <span className="font-mono tabular-nums text-[11px] text-fg-subtle">
          {new Date(g.started_at).toLocaleDateString()}
        </span>
      </Link>
    </li>
  );
}

function RecentBattles({ deckId }: { deckId: string }) {
  const battles = useQuery({
    queryKey: ["deck-battles", deckId],
    queryFn: () => getDeckBattles(deckId),
    enabled: !!deckId,
  });

  // Hidden entirely if the endpoint returns zero jobs — no value in
  // showing an empty section for a deck that hasn't been playtested.
  if (!battles.data || battles.data.count === 0) return null;

  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Recent duels
        </h2>
        <Link
          to="/playtest/history"
          className="text-[11px] text-fg-subtle hover:text-fg"
        >
          All history →
        </Link>
      </header>
      <ul className="surface divide-y divide-border">
        {battles.data.jobs.map((j) => (
          <BattleRow key={j.id} job={j} thisDeckId={deckId} />
        ))}
      </ul>
    </section>
  );
}

function BattleRow({ job, thisDeckId }: { job: DeckBattle; thisDeckId: string }) {
  const thisIsA = job.deck_a_id === thisDeckId;
  const opponentTheme = thisIsA ? job.deck_b_theme : job.deck_a_theme;
  const myWins = thisIsA ? job.outcome.deck_a_wins : job.outcome.deck_b_wins;
  const theirWins = thisIsA ? job.outcome.deck_b_wins : job.outcome.deck_a_wins;
  const won = myWins > theirWins;
  const tied = myWins === theirWins;

  return (
    <li>
      <Link
        to={`/playtest/jobs/${encodeURIComponent(job.id)}`}
        className="flex items-center gap-3 px-4 py-3 hover:bg-bg-raised/30 transition-colors"
      >
        <span
          className={cn(
            "font-mono tabular-nums text-sm w-16 text-right",
            won ? "text-accent" : tied ? "text-fg-muted" : "text-fg-muted",
          )}
        >
          {myWins}–{theirWins}
        </span>
        <span className="text-[11px] text-fg-subtle uppercase tracking-wider">vs</span>
        <span className="flex-1 text-sm truncate">{opponentTheme}</span>
        <span className="font-mono tabular-nums text-[11px] text-fg-subtle">
          {job.games}g · {new Date(job.started_at).toLocaleDateString()}
        </span>
      </Link>
    </li>
  );
}

function AskSection({
  deckId,
  sourceCollectionId,
  sourceCollectionName,
  isOwner,
  ownerUsername,
  publicAsks = false,
  consumePendingAsk,
}: {
  deckId: string;
  sourceCollectionId?: string;
  sourceCollectionName?: string;
  isOwner: boolean;
  ownerUsername: string | undefined;
  /** True when rendering inside DeckBody mode="public". Two effects:
   *   - submit button wraps in <GatedAction> for anonymous viewers
   *   - this AskSection skips the per-deck Past Questions list
   *     (a sibling <PublicAskHistory> renders that block via the
   *     unauthenticated /api/public/decks/:o/:s/asks endpoint —
   *     listDeckAsks here would 401 for anonymous viewers). */
  publicAsks?: boolean;
  /** Pulls a queued question out of the parent's pendingAsk slot
   *  (set by ActionReplayer after sign-up). Returns null when
   *  there's nothing pending. AskSection consumes it once on mount,
   *  pre-fills the textarea, and auto-fires start(). */
  consumePendingAsk?: () => string | null;
}) {
  const [cardPool, setCardPool] = useState<CardPool>(() =>
    readDeckCardPoolPreference(ownerUsername ?? "", deckId, isOwner && !!sourceCollectionId),
  );
  const selectCardPool = (pool: CardPool): void => {
    setCardPool(pool);
    writeDeckCardPoolPreference(ownerUsername ?? "", deckId, pool);
  };
  const [question, setQuestion] = useState("");
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [lastText, setLastText] = useState<string>("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  // Captured from the SSE `meta` event so the live AskAnswerCard can
  // render `prompt vX` while the answer is still streaming, before the
  // persisted row lands in the asks query refetch.
  const [livePromptVersion, setLivePromptVersion] = useState<string | null>(
    null,
  );
  // Question text currently being shown inline (running stream or
  // freshly-completed answer). Used to suppress the same entry from
  // Past Questions while it's the active inline answer — otherwise the
  // user sees the same Q&A twice once the history refetch lands.
  const [inlineQuestion, setInlineQuestion] = useState<string | null>(null);
  // Active conversation context. When set, the next question continues
  // this thread by linking parent_ask_id to its leaf. Cleared by the
  // "Start new thread" button. The on-screen `Continuing: …` chip
  // mirrors this state so the user always sees what they're replying
  // to before they hit Ask.
  const [activeThread, setActiveThread] = useState<
    | {
        thread_id: string;
        parent_ask_id: string;
        title: string;
      }
    | null
  >(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const refreshBalance = useRefreshBalance();
  const queryClient = useQueryClient();

  // Past Q&A thread for this deck, persisted server-side. Owner-only —
  // a public-deck visitor's questions are NOT persisted (see backend
  // ephemeral-ask path) so listDeckAsks would return only the owner's
  // private thread, which would be a privacy leak. In public mode the
  // sibling <PublicAskHistory> renders the same thread via the
  // unauthenticated public route instead.
  const { data: askHistory } = useQuery({
    queryKey: ["deck-asks", deckId],
    queryFn: () => listDeckAsks(deckId),
    staleTime: 60_000,
    enabled: isOwner && !publicAsks,
  });
  const allPriors: DeckAsk[] = isOwner && !publicAsks ? askHistory?.asks ?? [] : [];

  // Threads index. Owner mode reads via the authed route; public-deck
  // visitors fall through to <PublicAskHistory>'s own thread fetch
  // (one less ownership branch in this component).
  const { data: threadsData } = useQuery({
    queryKey: ["deck-threads", deckId],
    queryFn: () => listDeckThreads(deckId),
    staleTime: 60_000,
    enabled: isOwner && !publicAsks,
  });
  const threads: DeckThreadSummary[] =
    isOwner && !publicAsks ? threadsData?.threads ?? [] : [];
  // Drop the topmost prior matching the inline question so it doesn't
  // double-render. Match on question text (server normalizes whitespace
  // identically — and we still apply the same trim that `start` does).
  const priors: DeckAsk[] = inlineQuestion
    ? (() => {
        const target = inlineQuestion.trim();
        let dropped = false;
        return allPriors.filter((a) => {
          if (!dropped && a.question.trim() === target) {
            dropped = true;
            return false;
          }
          return true;
        });
      })()
    : allPriors;

  const reset = (): void => {
    setEvents([]);
    setLastText("");
    setAnswer(null);
    setElapsedMs(null);
    setError(null);
    setLivePromptVersion(null);
  };

  const start = async (overrideQuestion?: string): Promise<void> => {
    // Override path is for the post-signup replay: ActionReplayer
    // hands us the queued question and we don't want to depend on a
    // setQuestion()-induced re-render landing before this call. The
    // textarea state still gets set (so the user sees their question)
    // but the network call uses the override directly.
    const q = (overrideQuestion ?? question).trim();
    if (!q || running) return;
    reset();
    setInlineQuestion(q);
    setRunning(true);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await streamAskDeck(
        {
          deck_id: deckId,
          question: q,
          card_pool: isOwner && sourceCollectionId ? cardPool : "all",
          // Ephemeral mode: when viewing someone else's public deck,
          // pass owner_username so the backend resolves via the
          // public-decks path AND does not persist this turn.
          ...(!isOwner && ownerUsername ? { owner_username: ownerUsername } : {}),
          // Threading: when activeThread is set, this question is a
          // reply continuing that conversation. Server scopes the
          // prompt context to the ancestor chain instead of every
          // prior ask on the deck. Public-deck askers can't thread
          // (server rejects parent_ask_id in ephemeral mode).
          ...(isOwner && activeThread
            ? { parent_ask_id: activeThread.parent_ask_id }
            : {}),
          // Title only honored on the first ask of a thread. Derive
          // from the question itself — a 60-char truncation is
          // recognizable in the threads list without needing an LLM
          // labeling pass.
          ...(isOwner && !activeThread
            ? { thread_title: q.slice(0, 60) }
            : {}),
        },
        (msg) => {
          if (msg.kind === "meta") {
            // Server emits ask_prompt_version on the meta event so the
            // live card can label the in-flight answer with the prompt
            // version that produced it without waiting for the asks
            // refetch (~250ms after `done`).
            if (msg.data.ask_prompt_version) {
              setLivePromptVersion(msg.data.ask_prompt_version);
            }
          } else if (msg.kind === "event") {
            setEvents((prev) => [...prev, msg.data]);
            if (msg.data.type === "assistant_text_delta") {
              // Token deltas arrive from the Bedrock stream — accumulate
              // so the caret-following view paints as text is produced.
              const delta = msg.data.text;
              setLastText((prev) => prev + delta);
            } else if (msg.data.type === "turn_start") {
              // Reset the streaming buffer at turn boundaries so a tool
              // call mid-turn doesn't leave the pre-call prose dangling
              // in front of the post-call continuation.
              setLastText("");
            } else if (msg.data.type === "assistant_text") {
              // Fallback for any path that still emits whole-turn text
              // (non-streaming converse callers). Ignored in the streaming
              // case because deltas have already filled the buffer.
              if (msg.data.text.length > 0) setLastText(msg.data.text);
            }
          } else if (msg.kind === "done") {
            setAnswer(msg.data.answer);
            setElapsedMs(msg.data.elapsed_ms);
            // Ask charged ACTION_COSTS.ask_deck credits server-side.
            refreshBalance();
            // Advance the active thread to the new ask so the next
            // question continues this conversation by default. Title
            // for a brand-new thread comes from the question itself
            // (server already stored the truncated form).
            if (msg.data.ask_id && msg.data.thread_id) {
              setActiveThread({
                thread_id: msg.data.thread_id,
                parent_ask_id: msg.data.ask_id,
                title: activeThread?.title ?? q.slice(0, 60),
              });
            }
            // Owner-only: server persists the turn after `done`, so
            // refetch the asks + threads queries. Public-deck visitors
            // don't have a thread to refetch (ephemeral mode skips
            // persistence).
            if (isOwner) {
              setTimeout(() => {
                void queryClient.invalidateQueries({ queryKey: ["deck-asks", deckId] });
                void queryClient.invalidateQueries({ queryKey: ["deck-threads", deckId] });
              }, 250);
            }
          } else if (msg.kind === "error") {
            setError(msg.data.error);
            refreshBalance();
          }
        },
        ac.signal,
      );
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        setError((e as Error).message);
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const stop = (): void => {
    abortRef.current?.abort();
  };

  // Post-signup replay: if the parent's pendingAsk slot has a
  // question, pre-fill the textarea AND fire start() automatically.
  // We run this exactly once on mount (and then the ref guard keeps
  // it idempotent across re-renders triggered by setQuestion).
  // Without the ref guard, setQuestion would re-render this
  // component and the effect would peek a stale-but-cleared slot,
  // which is harmless but adds noise.
  const replayedRef = useRef(false);
  useEffect(() => {
    if (replayedRef.current) return;
    if (!consumePendingAsk) return;
    const queued = consumePendingAsk();
    if (!queued) return;
    replayedRef.current = true;
    setQuestion(queued);
    // Pass the queued question explicitly to start() — relying on
    // setQuestion()'s state update to land before start() reads
    // `question` via closure is racy (we'd be reading the previous
    // closure's stale `question`). Override path keeps the textarea
    // visually populated AND the request body using the right
    // string in the same call.
    void start(queued);
    // Intentionally narrow deps — consumePendingAsk is recreated on
    // every parent render but we only want to consume once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="space-y-3">
      <header>
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Ask about this deck
        </h2>
      </header>
      <div className="surface p-4 space-y-3">
        {isOwner && sourceCollectionId && (
          <DeckCardPoolControl
            mode={cardPool}
            onChange={selectCardPool}
            collectionName={sourceCollectionName}
            disabled={running}
          />
        )}
        {isOwner && (
          <div className="flex items-center gap-2 text-[11px] text-fg-subtle">
            {activeThread ? (
              <>
                <CornerDownRight size={12} strokeWidth={1.75} className="text-accent" />
                <span className="text-fg-muted">
                  Continuing:
                  <span className="ml-1 text-fg italic line-clamp-1 max-w-md align-middle inline-block">
                    {activeThread.title}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setActiveThread(null)}
                  className="ml-1 inline-flex items-center gap-1 text-fg-subtle hover:text-fg transition-colors"
                  title="Start a new thread instead — the next question won't carry this conversation's context."
                  disabled={running}
                >
                  <X size={11} strokeWidth={2} />
                  New thread
                </button>
              </>
            ) : (
              <>
                <Plus size={12} strokeWidth={1.75} className="text-accent" />
                <span>New thread</span>
              </>
            )}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="input min-h-[72px]"
          placeholder={
            activeThread
              ? "follow-up: e.g. what about against burn specifically?"
              : "e.g. why only 22 lands? how do I beat control?"
          }
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          disabled={running}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              void start();
            }
          }}
        />
        <div className="flex justify-between items-center">
          <div className="text-[11px] text-fg-subtle">
            Grounded in this deck's explanation. May consult card DB.
            {!isOwner && (
              <>
                <span className="hidden md:inline"> · </span>
                <span className="text-amber-700 dark:text-amber-400">
                  not saved (public deck)
                </span>
              </>
            )}
            <span className="hidden md:inline"> · ⌘⏎ to send</span>
          </div>
          {running ? (
            <button className="btn-primary text-xs" onClick={stop}>
              <Square size={13} strokeWidth={1.75} />
              Stop
            </button>
          ) : publicAsks ? (
            // In public mode the click MAY come from an anonymous
            // viewer — wrap the button in <GatedAction> so signed-in
            // viewers submit normally (passthrough), and signed-out
            // viewers queue the typed question + open the Clerk
            // modal. Post-signup, ActionReplayer pulls the question
            // back out and auto-submits via the consumePendingAsk
            // hook above.
            <GatedAction
              action="ask"
              payload={{ question: question.trim() }}
              deckId={deckId}
              owner={ownerUsername ?? ""}
              disabled={!question.trim()}
            >
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={!question.trim()}
                onClick={() => void start()}
              >
                <MessageSquare size={13} strokeWidth={1.75} />
                Ask
                <span className="text-[10px] font-mono opacity-70">· {ACTION_COSTS.ask_deck}</span>
              </button>
            </GatedAction>
          ) : (
            <button
              className="btn-primary text-xs"
              disabled={!question.trim()}
              onClick={() => void start()}
            >
              <MessageSquare size={13} strokeWidth={1.75} />
              Ask
              <span className="text-[10px] font-mono opacity-70">· {ACTION_COSTS.ask_deck}</span>
            </button>
          )}
        </div>
        {error && (
          <div className="text-xs text-danger">{error}</div>
        )}
      </div>

      {(running || answer || events.some((e) => e.type === "tool_call")) && (
        <AskAnswerCard
          events={events}
          lastText={lastText}
          answer={answer}
          elapsedMs={elapsedMs}
          running={running}
          // After persistence (~250ms after `done`), the matching ask
          // shows up in allPriors. Pass its id so the inline card can
          // show the Apply CTA. Only the deck owner ever has an
          // inlineQuestion — public-deck visitors can't apply.
          deckId={deckId}
          inlineAskId={
            isOwner && inlineQuestion
              ? allPriors.find(
                  (a) => a.question.trim() === inlineQuestion.trim(),
                )?.id ?? null
              : null
          }
          isOwner={isOwner}
          ownerUsername={ownerUsername}
          promptVersion={livePromptVersion}
        />
      )}

      {threads.length > 0 && (
        <ThreadList
          threads={threads}
          asks={priors}
          deckId={deckId}
          ownerUsername={ownerUsername}
          isOwner={isOwner}
          activeParentAskId={activeThread?.parent_ask_id ?? null}
          onReply={(ask) => {
            setActiveThread({
              thread_id: ask.thread_id,
              parent_ask_id: ask.id,
              title: priors.find((p) => p.thread_id === ask.thread_id)
                ?.thread_title
                ? priors.find((p) => p.thread_id === ask.thread_id)!
                    .thread_title!
                : ask.question.slice(0, 60),
            });
            // Pull the textarea into view + focus so the user lands
            // ready to type. Smooth scroll matches the rest of the
            // page's nav.
            setTimeout(() => {
              textareaRef.current?.focus();
              textareaRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 0);
          }}
        />
      )}
    </section>
  );
}

// Threaded conversation list. Replaces the flat "Past questions" view
// with one collapsible card per thread. Inside each card, messages are
// rendered chronologically with depth indentation reflecting the
// parent_ask_id chain — so explicit branches are visible without a
// canvas/svg tree. Each ask exposes a "Reply" button that the parent
// AskSection wires up to set activeThread + scroll to the textarea.
//
// Asks within a thread come from the parent's existing `asks` query
// (filtered client-side by thread_id) — no extra round-trip needed.
function ThreadList({
  threads,
  asks,
  deckId,
  ownerUsername,
  isOwner,
  activeParentAskId,
  onReply,
}: {
  threads: DeckThreadSummary[];
  asks: DeckAsk[];
  deckId: string;
  ownerUsername: string | undefined;
  isOwner: boolean;
  /** When set, the matching ask gets a subtle highlight so the user
   *  remembers which message their next question continues. */
  activeParentAskId: string | null;
  onReply: (ask: DeckAsk) => void;
}) {
  // Index asks by thread for O(1) lookup; preserve seq order within
  // each thread so the depth-based render walks the conversation
  // forward chronologically.
  const asksByThread = useMemo(() => {
    const map = new Map<string, DeckAsk[]>();
    for (const a of asks) {
      const list = map.get(a.thread_id) ?? [];
      list.push(a);
      map.set(a.thread_id, list);
    }
    for (const list of map.values()) {
      list.sort((x, y) => x.seq - y.seq);
    }
    return map;
  }, [asks]);

  return (
    <div className="space-y-2">
      <header className="flex items-center justify-between pt-1">
        <h3 className="text-[11px] font-mono uppercase tracking-widest text-fg-subtle inline-flex items-center gap-1.5">
          <MessagesSquare size={12} strokeWidth={1.75} />
          Conversations
        </h3>
        <span className="text-[11px] text-fg-subtle tabular-nums">
          {threads.length}
        </span>
      </header>
      <ul className="space-y-2">
        {threads.map((t, i) => {
          const messages = asksByThread.get(t.thread_id) ?? [];
          if (messages.length === 0) return null;
          return (
            <li key={t.thread_id}>
              <details
                className="group rounded-md border border-border/60 bg-bg-surface/30"
                open={i === 0}
              >
                <summary className="cursor-pointer list-none px-4 py-3 flex items-baseline justify-between gap-4 hover:bg-bg-raised/40">
                  <div className="min-w-0 flex items-baseline gap-3">
                    <span className="font-mono text-[10px] tabular-nums text-fg-subtle">
                      {t.message_count}{" "}
                      {t.message_count === 1 ? "msg" : "msgs"}
                    </span>
                    <span className="text-sm text-fg truncate">
                      {t.title}
                    </span>
                  </div>
                  <div className="shrink-0 text-[11px] text-fg-subtle tabular-nums">
                    {new Date(t.last_activity_at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </summary>
                <div className="border-t border-border/40 divide-y divide-border/40">
                  {messages.map((a) => (
                    <ThreadMessage
                      key={a.id}
                      ask={a}
                      depth={depthFromAncestry(messages, a)}
                      isActiveParent={a.id === activeParentAskId}
                      isOwner={isOwner}
                      ownerUsername={ownerUsername}
                      deckId={deckId}
                      onReply={onReply}
                    />
                  ))}
                </div>
              </details>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Walk parent_ask_id back to a thread root within `messages` to derive
 *  the indent depth. Linear conversations all return 0; replies that
 *  branch off mid-thread render at depth 1+. */
function depthFromAncestry(messages: DeckAsk[], target: DeckAsk): number {
  const byId = new Map(messages.map((m) => [m.id, m]));
  let depth = 0;
  let cur: DeckAsk | undefined = target;
  while (cur?.parent_ask_id) {
    const parent = byId.get(cur.parent_ask_id);
    if (!parent) break;
    depth += 1;
    cur = parent;
    if (depth > 16) break; // defensive — should never happen
  }
  return depth;
}

function ThreadMessage({
  ask,
  depth,
  isActiveParent,
  isOwner,
  ownerUsername,
  deckId,
  onReply,
}: {
  ask: DeckAsk;
  depth: number;
  isActiveParent: boolean;
  isOwner: boolean;
  ownerUsername: string | undefined;
  deckId: string;
  onReply: (ask: DeckAsk) => void;
}) {
  // Cap visual indent at ~3 levels — deeper branches still walk the
  // parent chain (server-side context is correct), but the UI doesn't
  // keep shrinking the message column.
  const indentPx = Math.min(depth, 3) * 16;
  return (
    <div
      className={cn(
        "px-4 py-3 space-y-2",
        isActiveParent && "bg-accent/5 border-l-2 border-l-accent",
      )}
      style={{ paddingLeft: `${16 + indentPx}px` }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0 flex items-baseline gap-2 text-[11px] text-fg-subtle">
          <span className="font-mono tabular-nums">Q{ask.seq}</span>
          {depth > 0 && (
            <CornerDownRight
              size={11}
              strokeWidth={2}
              className="text-fg-subtle/70"
            />
          )}
          <span className="text-sm text-fg leading-snug line-clamp-2">
            {ask.question}
          </span>
        </div>
        <div className="shrink-0 flex items-baseline gap-2 text-[11px] text-fg-subtle tabular-nums">
          {ask.prompt_version && (
            <span
              className="font-mono"
              title="Ask-framing prompt version that produced this answer"
            >
              prompt {ask.prompt_version}
            </span>
          )}
          <span>
            {new Date(ask.asked_at).toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>
      <div className="prose-sm text-sm leading-relaxed">
        <Markdown text={ask.answer} />
      </div>
      {isOwner && ownerUsername && (
        <div className="flex items-center gap-3 pt-1 text-xs">
          <button
            type="button"
            onClick={() => onReply(ask)}
            className="inline-flex items-center gap-1.5 text-accent hover:underline"
            title="Continue this conversation. Next question will be linked as a reply."
          >
            <CornerDownRight size={12} strokeWidth={1.75} />
            Reply
          </button>
          <Link
            to={`/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(deckId)}/edit?from_ask=${encodeURIComponent(ask.id)}`}
            className="inline-flex items-center gap-1.5 text-fg-muted hover:text-fg hover:underline"
            title="Open editor with these suggestions pre-filled. Save is free."
          >
            <Pencil size={12} strokeWidth={1.75} />
            Apply suggested changes
          </Link>
        </div>
      )}
    </div>
  );
}

function AskAnswerCard({
  events,
  lastText,
  answer,
  elapsedMs,
  running,
  deckId,
  inlineAskId,
  isOwner,
  ownerUsername,
  promptVersion,
}: {
  events: AgentEvent[];
  lastText: string;
  answer: string | null;
  elapsedMs: number | null;
  running: boolean;
  deckId: string;
  inlineAskId: string | null;
  isOwner: boolean;
  ownerUsername: string | undefined;
  /** Ask-framing prompt version that produced (or is producing) this
   *  answer. Captured from the SSE meta event for live runs; null
   *  while we're still waiting on meta. */
  promptVersion: string | null;
}) {
  // Tool calls are rare in ask (most questions answer from deck context
  // alone), but when they happen, surface them compactly so the user
  // sees the agent didn't just vanish for 10s mid-think.
  const toolCalls = events.filter(
    (e): e is Extract<AgentEvent, { type: "tool_call" }> => e.type === "tool_call",
  );

  // Body = streamed text while running, final answer when done. They're
  // the same content in the no-tool-call case, so showing both is just
  // visual redundancy. Prefer `answer` once present; fall back to the
  // streaming `lastText` while we wait.
  const body = answer ?? lastText;

  return (
    <div className="surface p-4 space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-fg-muted">
          <MessageSquare size={14} strokeWidth={1.75} />
          Answer
          {running && (
            <span className="flex items-center gap-1.5 text-accent">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              thinking
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-2 text-[11px] text-fg-subtle font-mono tabular-nums">
          {promptVersion && (
            <span title="Ask-framing prompt version that produced this answer">
              prompt {promptVersion}
            </span>
          )}
          {elapsedMs != null && (
            <span>{(elapsedMs / 1000).toFixed(1)}s</span>
          )}
        </div>
      </header>

      {toolCalls.length > 0 && (
        <ul className="space-y-1 text-[11px] font-mono">
          {toolCalls.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-accent">
              <Wrench size={11} strokeWidth={2} className="mt-0.5 shrink-0" />
              <span>
                <span className="font-medium">{t.name}</span>{" "}
                <span className="text-fg-subtle">
                  {truncateJson(t.input, 80)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {body ? (
        <div className="prose-sm text-sm leading-relaxed space-y-3">
          <Markdown text={body} />
          {running && <BlinkingCaret />}
        </div>
      ) : running ? (
        <ThinkingDots />
      ) : null}

      {/* Apply CTA appears only after the answer is persisted (we
          have an ask_id from the refetched history) AND the caller
          owns the deck. Save itself is free — strategy regen is a
          separate explicit action with its own price. */}
      {!running && answer && isOwner && inlineAskId && ownerUsername && (
        <div className="pt-2 border-t border-border/40">
          <Link
            to={`/decks/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(deckId)}/edit?from_ask=${encodeURIComponent(inlineAskId)}`}
            className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
            title="Open editor with these suggestions pre-filled. Save is free."
          >
            <Pencil size={13} strokeWidth={1.75} />
            Apply suggested changes
          </Link>
        </div>
      )}
    </div>
  );
}

function truncateJson(input: unknown, max: number): string {
  try {
    const s = JSON.stringify(input);
    return s.length > max ? `${s.slice(0, max)}…` : s;
  } catch {
    return "";
  }
}

function BlinkingCaret() {
  return (
    <span className="inline-block w-[2px] h-[1em] align-[-2px] bg-accent ml-0.5 animate-pulse" />
  );
}

function ThinkingDots() {
  return (
    <div className="flex items-center gap-1 text-fg-subtle text-sm py-2">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-fg-subtle animate-bounce [animation-delay:-0.3s]" />
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-fg-subtle animate-bounce [animation-delay:-0.15s]" />
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-fg-subtle animate-bounce" />
    </div>
  );
}

// Copies the parsed deck as MTGA-import-ready Arena format. We serialize
// from the parsed Deck (not data.answer) because the stored answer wraps
// the decklist in ``` fences for parseArenaDeck — fences break MTGA's
// paste import.
function CopyDeckButton({ deck, name }: { deck: import("../lib/api").Deck; name?: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(serializeDeckForArena(deck, name));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API failed (e.g. insecure context). Fail silently —
      // the user will notice the button didn't flash.
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
      title="Copy decklist in Arena import format"
    >
      {copied ? (
        <>
          <Check size={13} strokeWidth={2} />
          Copied
        </>
      ) : (
        <>
          <Copy size={13} strokeWidth={1.75} />
          Copy deck
        </>
      )}
    </button>
  );
}

// Owner-only inline toggle that flips decks.is_public on the deck row.
// meta_reference decks are intentionally excluded one level up — those
// are seeded artifacts, not user-managed.
function VisibilityToggle({
  slug,
  isPublic,
}: {
  slug: string;
  isPublic: boolean;
}) {
  const queryClient = useQueryClient();
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const mutation = useMutation({
    mutationFn: (next: boolean) => setDeckVisibility(slug, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deck-detail", slug] });
      void queryClient.invalidateQueries({ queryKey: ["decks"] });
      void queryClient.invalidateQueries({ queryKey: ["public-decks"] });
    },
    onError: () => {
      setOptimistic(null);
    },
  });
  const effective = optimistic ?? isPublic;
  return (
    <button
      type="button"
      onClick={() => {
        const next = !effective;
        setOptimistic(next);
        mutation.mutate(next);
      }}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors disabled:opacity-50"
      title={effective ? "Click to unpublish" : "Click to publish for others to find"}
    >
      {effective ? (
        <>
          <Globe size={13} strokeWidth={1.75} />
          Public
        </>
      ) : (
        <>
          <Lock size={13} strokeWidth={1.75} />
          Private
        </>
      )}
    </button>
  );
}

// Rendered when the slug a user typed in /decks/:id resolves to multiple
// public decks across users. The first match was loaded as the page;
// these are the alternatives, surfaced as a compact chooser at the
// bottom of the page so the user can switch.
function PublicMatchesSwitcher({
  matches,
  currentSlug,
}: {
  matches: Array<{ owner_username: string; slug: string; theme: string }>;
  currentSlug: string;
}) {
  // Render-only for now — there's no dedicated /decks/public/:owner/:slug
  // SPA route yet (it lands in Layer D's deck-list rework). The chooser
  // fires only on slug collisions across public decks, which is rare;
  // when Layer D ships, swap the <span> for a <Link> to that route.
  if (matches.length === 0) return null;
  return (
    <section className="surface p-4 space-y-2">
      <header className="text-[11px] uppercase tracking-wider text-fg-subtle">
        Other public decks named "{currentSlug}"
      </header>
      <ul className="space-y-1">
        {matches.map((m) => (
          <li key={`${m.owner_username}/${m.slug}`} className="text-sm">
            <span className="font-mono text-fg-muted">
              {m.owner_username}/{m.slug}
            </span>
            <span className="ml-2 text-fg-subtle">{m.theme}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Owner-only banner that surfaces when the latest deck_versions row's
// source is a card-list edit (manual_edit, apply_ask, upload, revert)
// rather than a fresh prose generation (build, regenerate). Drives the
// "Regenerate strategy" CTA per Plans/REGEN-STRATEGY.md. Reuses the
// VersionTimeline's `["deck-versions", id]` query so the fetch is
// shared. Renders nothing during loading or when the latest row is
// already a build/regenerate (no staleness).
function RegenStrategySection({ deckId }: { deckId: string }) {
  const qc = useQueryClient();
  const refreshBalance = useRefreshBalance();
  const versionsQuery = useQuery({
    queryKey: ["deck-versions", deckId],
    queryFn: () => listDeckVersions(deckId),
    enabled: !!deckId,
  });
  const regenMut = useMutation({
    mutationFn: () => regenerateDeckStrategy(deckId),
    onSuccess: () => {
      refreshBalance();
      void qc.invalidateQueries({ queryKey: ["deck", deckId] });
      void qc.invalidateQueries({ queryKey: ["deck-detail"] });
      void qc.invalidateQueries({ queryKey: ["deck-versions", deckId] });
      void qc.invalidateQueries({ queryKey: ["decks"] });
    },
  });

  const versions = versionsQuery.data?.versions ?? [];
  if (versions.length === 0) return null;
  const current = versions[0]!;
  const isFreshProse =
    current.source === "build" || current.source === "regenerate";
  if (isFreshProse) return null;

  // Walk back to find the most recent build/regenerate row — that's
  // the version the prose was actually written for.
  const proseVersion =
    versions.find(
      (v) => v.source === "build" || v.source === "regenerate",
    ) ?? null;

  return (
    <section className="surface p-4 flex flex-wrap items-center gap-3 border-warn/30">
      <RefreshCw size={14} strokeWidth={1.75} className="text-warn shrink-0" />
      <p className="text-sm text-fg-muted leading-relaxed flex-1 min-w-[14rem]">
        Strategy notes were generated for{" "}
        <span className="font-mono text-fg">
          v{proseVersion?.version_no ?? 1}
        </span>
        . Your current deck is{" "}
        <span className="font-mono text-fg">v{current.version_no}</span>.
      </p>
      <button
        type="button"
        onClick={() => regenMut.mutate()}
        disabled={regenMut.isPending}
        className="btn-secondary text-xs h-9 px-3 inline-flex items-center gap-1.5"
        title="Re-run the analysis prompt against the current card list. One Opus turn."
      >
        <RefreshCw size={13} strokeWidth={1.75} />
        {regenMut.isPending ? "Regenerating…" : "Regenerate strategy"}
        <span className="text-[10px] font-mono opacity-70">
          · {ACTION_COSTS.regenerate_strategy}
        </span>
      </button>
      {regenMut.error && (
        <div className="basis-full text-xs text-danger">
          {(regenMut.error as Error).message}
        </div>
      )}
    </section>
  );
}
